/**
 * Visuals for the "אחרי שליחה" screen — 10 animations + the org's own image.
 * Pure CSS/SVG (no deps), deterministic particle layouts (no jitter on
 * re-render), and everything stops under prefers-reduced-motion.
 *
 * Each visual is two parts:
 *  - VisualHero     — the centrepiece at the top of the card
 *  - VisualBackdrop — an optional full-screen layer behind the card
 * `compact` renders both small, for the picker tiles in the settings window.
 */
import React, { useMemo } from 'react';

export type VisualKind =
  | 'check' | 'confetti' | 'hearts' | 'fireworks' | 'balloons'
  | 'sparkles' | 'heartbeat' | 'gift' | 'coins' | 'candle' | 'image';

export const AFTER_SUBMIT_VISUALS: { id: VisualKind; label: string }[] = [
  { id: 'check', label: 'וי מונפש' },
  { id: 'confetti', label: 'קונפטי' },
  { id: 'hearts', label: 'לבבות עולים' },
  { id: 'fireworks', label: 'זיקוקים' },
  { id: 'balloons', label: 'בלונים' },
  { id: 'sparkles', label: 'נצנוצים' },
  { id: 'heartbeat', label: 'לב פועם' },
  { id: 'gift', label: 'מתנה נפתחת' },
  { id: 'coins', label: 'מטבעות לקופה' },
  { id: 'candle', label: 'נר דולק' },
  { id: 'image', label: 'תמונה משלכם' },
];

const PALETTE = ['#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#facc15'];

export const VISUALS_CSS = `
@keyframes amtvFall { 0% { transform: translate3d(0,-12vh,0) rotate(0) } 100% { transform: translate3d(var(--dx,0),112vh,0) rotate(var(--rot,540deg)) } }
@keyframes amtvRise { 0% { transform: translate3d(0,0,0) scale(.6); opacity: 0 } 12% { opacity: .9 } 100% { transform: translate3d(var(--dx,0),-115vh,0) scale(1.1); opacity: 0 } }
@keyframes amtvSway { 0%,100% { margin-inline-start: -8px } 50% { margin-inline-start: 8px } }
@keyframes amtvBurst { 0% { transform: translate(0,0) scale(1); opacity: 1 } 70% { opacity: 1 } 100% { transform: translate(var(--bx),var(--by)) scale(.3); opacity: 0 } }
@keyframes amtvTwinkle { 0%,100% { transform: scale(.2) rotate(0); opacity: 0 } 50% { transform: scale(1) rotate(90deg); opacity: 1 } }
@keyframes amtvBeat { 0%,40%,100% { transform: scale(1) } 15% { transform: scale(1.18) } 28% { transform: scale(1.06) } }
@keyframes amtvRing { 0% { transform: scale(.4); opacity: 0 } 60% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
@keyframes amtvDraw { from { stroke-dashoffset: 32 } to { stroke-dashoffset: 0 } }
@keyframes amtvLid { 0%,20% { transform: translateY(0) rotate(0) } 45%,80% { transform: translateY(-14px) rotate(-14deg) } 100% { transform: translateY(0) rotate(0) } }
@keyframes amtvPop { 0%,25% { transform: translateY(8px) scale(0); opacity: 0 } 50%,78% { transform: translateY(-22px) scale(1); opacity: 1 } 100% { transform: translateY(-30px) scale(.6); opacity: 0 } }
@keyframes amtvCoin { 0% { transform: translateY(-46px); opacity: 0 } 15% { opacity: 1 } 55% { transform: translateY(2px); opacity: 1 } 70%,100% { transform: translateY(10px); opacity: 0 } }
@keyframes amtvJar { 0%,50%,100% { transform: scale(1) } 58% { transform: scale(1.05,.95) } }
@keyframes amtvFlame { 0%,100% { transform: scale(1,1) skewX(0) } 25% { transform: scale(.94,1.06) skewX(2deg) } 50% { transform: scale(1.04,.95) skewX(-2deg) } 75% { transform: scale(.97,1.04) skewX(1deg) } }
@keyframes amtvGlow { 0%,100% { opacity: .55; transform: scale(1) } 50% { opacity: .85; transform: scale(1.12) } }
@keyframes amtvImg { 0% { opacity: 0; transform: scale(.9) } 100% { opacity: 1; transform: scale(1) } }
/* picker tiles are ~96px tall — vh-based travel would overshoot */
@keyframes amtvFallC { 0% { transform: translate3d(0,-10px,0) rotate(0) } 100% { transform: translate3d(0,100px,0) rotate(360deg) } }
@keyframes amtvRiseC { 0% { transform: translateY(0) scale(.6); opacity: 0 } 15% { opacity: .9 } 100% { transform: translateY(-120px) scale(1); opacity: 0 } }
@keyframes amtvRiseBC { 0% { transform: translateY(0); opacity: 0 } 15% { opacity: 1 } 100% { transform: translateY(-130px); opacity: 0 } }
@media (prefers-reduced-motion: reduce) {
  .amtv, .amtv * { animation: none !important }
  .amtv-particle { display: none }
}
`;

/** Deterministic 0..1 sequence — same layout on every render. */
function seq(n: number, salt: number) {
  const out: number[] = [];
  let x = (salt * 9301 + 49297) % 233280;
  for (let i = 0; i < n; i++) { x = (x * 9301 + 49297) % 233280; out.push(x / 233280); }
  return out;
}

const Heart: React.FC<{ size: number; color: string; style?: React.CSSProperties }> = ({ size, color, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} aria-hidden>
    <path fill={color} d="M12 21s-7.5-4.6-9.8-9.2C.6 8.4 2.4 4.5 6.1 4.1c2.2-.2 3.9 1 5.9 3 2-2 3.7-3.2 5.9-3 3.7.4 5.5 4.3 3.9 7.7C19.5 16.4 12 21 12 21z" />
  </svg>
);

// ── backdrops ────────────────────────────────────────────────────────────────

export const VisualBackdrop: React.FC<{ kind: VisualKind; primary: string; compact?: boolean }> = ({ kind, primary, compact }) => {
  const layer: React.CSSProperties = { position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 };
  const parts = useMemo(() => {
    if (kind === 'confetti') {
      const r = seq(compact ? 16 : 70, 7);
      return r.map((v, i) => ({
        left: `${(v * 100).toFixed(1)}%`, delay: (seq(1, i + 3)[0] * (compact ? 2.4 : 3.5)).toFixed(2),
        dur: (2.6 + seq(1, i + 11)[0] * 2.6).toFixed(2), color: PALETTE[i % PALETTE.length],
        w: compact ? 4 : 7 + Math.round(v * 5), round: i % 3 === 0, dx: `${Math.round((v - .5) * 120)}px`,
      }));
    }
    if (kind === 'hearts') {
      const r = seq(compact ? 7 : 18, 13);
      return r.map((v, i) => ({
        left: `${(4 + v * 92).toFixed(1)}%`, delay: (seq(1, i + 5)[0] * 5).toFixed(2), dur: (4.5 + seq(1, i + 9)[0] * 3).toFixed(2),
        size: compact ? 9 : 16 + Math.round(v * 18), color: i % 3 === 0 ? primary : ['#f43f5e', '#fb7185', '#ec4899'][i % 3], dx: `${Math.round((v - .5) * 80)}px`,
      }));
    }
    if (kind === 'balloons') {
      const r = seq(compact ? 5 : 11, 21);
      return r.map((v, i) => ({
        left: `${(3 + v * 94).toFixed(1)}%`, delay: (seq(1, i + 2)[0] * 6).toFixed(2), dur: (7 + seq(1, i + 4)[0] * 4).toFixed(2),
        size: compact ? 14 : 34 + Math.round(v * 20), color: [primary, ...PALETTE][i % (PALETTE.length + 1)],
      }));
    }
    if (kind === 'fireworks') {
      const spots = compact
        ? [{ x: 22, y: 30 }, { x: 78, y: 40 }]
        : [{ x: 15, y: 22 }, { x: 82, y: 18 }, { x: 70, y: 70 }, { x: 25, y: 75 }, { x: 50, y: 12 }];
      return spots.map((s, i) => ({ ...s, delay: (i * .7).toFixed(2), color: PALETTE[(i * 2) % PALETTE.length] }));
    }
    return [];
  }, [kind, primary, compact]);

  if (kind === 'confetti') {
    return (
      <div className="amtv" style={layer} aria-hidden>
        {(parts as any[]).map((p, i) => (
          <span key={i} className="amtv-particle" style={{
            position: 'absolute', top: 0, left: p.left, width: p.w, height: p.round ? p.w : p.w * 1.6,
            background: p.color, borderRadius: p.round ? '50%' : 2, ['--dx' as any]: p.dx,
            // compact tiles are ~90px tall — the vh-based fall would overshoot
            animation: `${compact ? 'amtvFallC' : 'amtvFall'} ${p.dur}s linear ${p.delay}s ${compact ? 'infinite' : 3} both`,
          }} />
        ))}
      </div>
    );
  }
  if (kind === 'hearts') {
    return (
      <div className="amtv" style={layer} aria-hidden>
        {(parts as any[]).map((p, i) => (
          <span key={i} className="amtv-particle" style={{
            position: 'absolute', bottom: -30, left: p.left, ['--dx' as any]: p.dx,
            animation: `${compact ? 'amtvRiseC' : 'amtvRise'} ${p.dur}s ease-in ${p.delay}s infinite both`,
          }}><Heart size={p.size} color={p.color} /></span>
        ))}
      </div>
    );
  }
  if (kind === 'balloons') {
    return (
      <div className="amtv" style={layer} aria-hidden>
        {(parts as any[]).map((p, i) => (
          <span key={i} className="amtv-particle" style={{
            position: 'absolute', bottom: -p.size * 2, left: p.left,
            animation: `${compact ? 'amtvRiseBC' : 'amtvRise'} ${p.dur}s linear ${p.delay}s infinite both`,
          }}>
            <span style={{ display: 'block', animation: 'amtvSway 3s ease-in-out infinite' }}>
              <svg width={p.size} height={p.size * 1.7} viewBox="0 0 40 68" aria-hidden>
                <ellipse cx="20" cy="20" rx="17" ry="20" fill={p.color} />
                <ellipse cx="13" cy="12" rx="4" ry="6" fill="#fff" opacity=".35" />
                <path d="M20 40 l-3 4 h6 z" fill={p.color} />
                <path d="M20 44 C 14 52, 26 58, 19 68" stroke="#9ca3af" strokeWidth="1.2" fill="none" />
              </svg>
            </span>
          </span>
        ))}
      </div>
    );
  }
  if (kind === 'fireworks') {
    const rays = compact ? 8 : 14;
    const dist = compact ? 22 : 95;
    return (
      <div className="amtv" style={layer} aria-hidden>
        {(parts as any[]).map((b, i) => (
          <span key={i} style={{ position: 'absolute', left: `${b.x}%`, top: `${b.y}%` }}>
            {Array.from({ length: rays }).map((_, k) => {
              const a = (k / rays) * Math.PI * 2;
              return (
                <span key={k} className="amtv-particle" style={{
                  position: 'absolute', width: compact ? 3 : 6, height: compact ? 3 : 6, borderRadius: '50%',
                  background: k % 2 ? b.color : PALETTE[(i + k) % PALETTE.length],
                  boxShadow: `0 0 ${compact ? 3 : 8}px ${b.color}`,
                  ['--bx' as any]: `${Math.round(Math.cos(a) * dist)}px`, ['--by' as any]: `${Math.round(Math.sin(a) * dist)}px`,
                  animation: `amtvBurst 1.6s cubic-bezier(.1,.8,.3,1) ${b.delay}s infinite both`,
                }} />
              );
            })}
          </span>
        ))}
      </div>
    );
  }
  return null;
};

// ── heroes ───────────────────────────────────────────────────────────────────

const CheckRing: React.FC<{ primary: string; size: number }> = ({ primary, size }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: `linear-gradient(135deg, ${primary}, ${primary}BB)`, boxShadow: `0 ${size / 7}px ${size / 3}px ${primary}55`,
    animation: 'amtvRing .45s cubic-bezier(.16,1,.3,1) both',
  }}>
    <svg width={size / 2} height={size / 2} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 13l4 4L19 7" style={{ strokeDasharray: 32, animation: 'amtvDraw .45s .2s ease-out both' }} />
    </svg>
  </div>
);

export const VisualHero: React.FC<{ kind: VisualKind; primary: string; imageUrl?: string; compact?: boolean }> = ({ kind, primary, imageUrl, compact }) => {
  const size = compact ? 40 : 84;
  const wrap: React.CSSProperties = { position: 'relative', width: size * 1.6, height: size * 1.2, margin: `0 auto ${compact ? 0 : 16}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' };

  if (kind === 'image') {
    if (!imageUrl) return <div className="amtv" style={wrap}><CheckRing primary={primary} size={size} /></div>;
    return (
      <div className="amtv" style={{ margin: `0 auto ${compact ? 0 : 18}px`, textAlign: 'center' }}>
        <img src={imageUrl} alt="" style={{
          maxWidth: '100%', maxHeight: compact ? 56 : 220, borderRadius: compact ? 8 : 18, objectFit: 'cover',
          boxShadow: compact ? 'none' : '0 12px 30px rgba(15,23,42,.18)', animation: 'amtvImg .6s cubic-bezier(.16,1,.3,1) both',
        }} />
      </div>
    );
  }

  if (kind === 'sparkles') {
    const stars = [{ x: -8, y: 6, d: 0 }, { x: 88, y: 0, d: .5 }, { x: 96, y: 70, d: 1 }, { x: 0, y: 80, d: 1.4 }, { x: 45, y: -12, d: .8 }];
    return (
      <div className="amtv" style={wrap}>
        <CheckRing primary={primary} size={size} />
        {stars.map((s, i) => (
          <svg key={i} width={size / 3.4} height={size / 3.4} viewBox="0 0 24 24" aria-hidden style={{
            position: 'absolute', left: `${s.x}%`, top: `${s.y}%`, animation: `amtvTwinkle 1.8s ease-in-out ${s.d}s infinite both`,
          }}>
            <path fill={i % 2 ? '#facc15' : primary} d="M12 0 C13 8 16 11 24 12 C16 13 13 16 12 24 C11 16 8 13 0 12 C8 11 11 8 12 0z" />
          </svg>
        ))}
      </div>
    );
  }

  if (kind === 'heartbeat') {
    return (
      <div className="amtv" style={wrap}>
        <div style={{ position: 'absolute', width: size * 1.1, height: size * 1.1, borderRadius: '50%', background: '#f43f5e22', animation: 'amtvGlow 1.4s ease-in-out infinite' }} />
        <Heart size={size * 1.05} color="#f43f5e" style={{ animation: 'amtvBeat 1.4s ease-in-out infinite', filter: 'drop-shadow(0 8px 14px #f43f5e55)' }} />
      </div>
    );
  }

  if (kind === 'gift') {
    return (
      <div className="amtv" style={wrap}>
        <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden style={{ overflow: 'visible' }}>
          <g style={{ transformOrigin: '32px 28px', animation: 'amtvPop 2.6s ease-in-out infinite both' }}>
            <path fill="#facc15" d="M32 10 C33.4 17 36 19.6 43 21 C36 22.4 33.4 25 32 32 C30.6 25 28 22.4 21 21 C28 19.6 30.6 17 32 10z" />
          </g>
          <rect x="12" y="32" width="40" height="26" rx="3" fill={primary} />
          <rect x="29" y="32" width="6" height="26" fill="#fff" opacity=".85" />
          <g style={{ transformOrigin: '12px 30px', animation: 'amtvLid 2.6s ease-in-out infinite both' }}>
            <rect x="9" y="24" width="46" height="10" rx="3" fill={primary} style={{ filter: 'brightness(1.12)' }} />
            <rect x="29" y="24" width="6" height="10" fill="#fff" opacity=".85" />
            <path d="M32 24 C26 14 16 18 22 24 M32 24 C38 14 48 18 42 24" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
          </g>
        </svg>
      </div>
    );
  }

  if (kind === 'coins') {
    return (
      <div className="amtv" style={wrap}>
        <svg width={size * 1.1} height={size * 1.1} viewBox="10 4 44 58" aria-hidden style={{ overflow: 'visible' }}>
          {[0, .8, 1.6].map((d, i) => (
            <g key={i} style={{ animation: `amtvCoin 2.4s ease-in ${d}s infinite both` }}>
              <ellipse cx={26 + i * 6} cy="14" rx="7" ry="7" fill="#f59e0b" />
              <ellipse cx={26 + i * 6} cy="14" rx="4.6" ry="4.6" fill="#fcd34d" />
              <text x={26 + i * 6} y="17" textAnchor="middle" fontSize="7" fontWeight="900" fill="#b45309">₪</text>
            </g>
          ))}
          <g style={{ transformOrigin: '32px 60px', animation: 'amtvJar .8s ease-in-out infinite' }}>
            <rect x="14" y="26" width="36" height="34" rx="8" fill={primary} />
            <rect x="20" y="24" width="24" height="5" rx="2.5" fill="#1f2937" opacity=".55" />
            <path d="M24 42 h16" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity=".9" />
          </g>
        </svg>
      </div>
    );
  }

  if (kind === 'candle') {
    return (
      <div className="amtv" style={wrap}>
        <svg width={size} height={size * 1.1} viewBox="0 0 64 70" aria-hidden style={{ overflow: 'visible' }}>
          <circle cx="32" cy="20" r="16" fill="#fde68a" style={{ transformOrigin: '32px 20px', animation: 'amtvGlow 2s ease-in-out infinite' }} />
          <g style={{ transformOrigin: '32px 32px', animation: 'amtvFlame 1.1s ease-in-out infinite' }}>
            <path d="M32 6 C38 16 40 22 32 32 C24 22 26 16 32 6z" fill="#f59e0b" />
            <path d="M32 14 C35 19 36 23 32 29 C28 23 29 19 32 14z" fill="#fef3c7" />
          </g>
          <line x1="32" y1="31" x2="32" y2="35" stroke="#374151" strokeWidth="1.6" />
          <rect x="24" y="35" width="16" height="30" rx="3" fill="#fff" stroke={primary} strokeWidth="2" />
          <path d="M26 40 q2 4 0 8" stroke={primary} strokeWidth="1.5" fill="none" opacity=".4" />
        </svg>
      </div>
    );
  }

  // check / confetti / fireworks / balloons / hearts → the check ring (hearts
  // gets a heart so it matches the backdrop).
  if (kind === 'hearts') {
    return <div className="amtv" style={wrap}><Heart size={size} color="#f43f5e" style={{ animation: 'amtvRing .5s cubic-bezier(.16,1,.3,1) both', filter: 'drop-shadow(0 8px 14px #f43f5e55)' }} /></div>;
  }
  return <div className="amtv" style={wrap}><CheckRing primary={primary} size={size} /></div>;
};

/** Tile used by the settings window's picker. */
export const VisualTile: React.FC<{ kind: VisualKind; label: string; primary: string; imageUrl?: string; active: boolean; onClick: () => void }> = ({ kind, label, primary, imageUrl, active, onClick }) => (
  <button type="button" onClick={onClick} title={label} style={{
    position: 'relative', overflow: 'hidden', height: 96, borderRadius: 12, cursor: 'pointer', padding: 0, fontFamily: 'inherit',
    border: active ? '2px solid #2563eb' : '1px solid #e5e7eb', background: active ? '#eff6ff' : '#fafafa',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
  }}>
    <VisualBackdrop kind={kind} primary={primary} compact />
    <div style={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', alignItems: 'center' }}>
      {kind === 'image' && !imageUrl
        ? <span style={{ fontSize: 26 }}>🖼️</span>
        : <VisualHero kind={kind} primary={primary} imageUrl={imageUrl} compact />}
    </div>
    <span style={{ position: 'relative', zIndex: 1, fontSize: 11.5, fontWeight: 700, color: active ? '#1d4ed8' : '#374151', padding: '0 4px 6px' }}>{label}</span>
  </button>
);
